import React, { useEffect, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ApiClientProvider } from '../contexts/ApiClientContext'
import { AppContent } from '../components/AppContent'
import { ShadowContentDetector } from './content-detector'
import { shadowLogger } from '../utils/logger'
import { queryClient } from '../lib/utils'
import type { ShadowEmbedConfig } from './types'

interface ArticleContent {
  title: string
  content: string
  author?: string
  url: string
}

interface EmbedWidgetConfig {
  projectId: string
  theme: 'light' | 'dark' | 'auto'
  width: string
  height: string
  primaryColor?: string
  position: 'inline' | 'bottom-right' | 'bottom-left'
  autoDetectContent: boolean
  language?: 'eng' | 'heb' | 'arb'
  direction?: 'ltr' | 'rtl' | 'auto'
  mobileFloatingBar?: boolean
  bottomOffset?: string
}

interface ShadowAppProps {
  config: ShadowEmbedConfig
  onResize?: (height: number) => void
  onOpenLink?: (url: string) => void
  onCenterRequest?: (height: number) => void
}

/**
 * Convert ShadowEmbedConfig to EmbedWidgetConfig for AppContent compatibility
 */
function convertConfig(shadowConfig: ShadowEmbedConfig): EmbedWidgetConfig {
  return {
    projectId: shadowConfig.projectId,
    theme: shadowConfig.theme || 'light',
    width: shadowConfig.width || '100%',
    height: shadowConfig.height || 'auto',
    primaryColor: shadowConfig.primaryColor,
    position: shadowConfig.position || 'inline',
    autoDetectContent: shadowConfig.autoDetectContent ?? true,
    language: shadowConfig.language,
    direction: shadowConfig.direction,
    mobileFloatingBar: shadowConfig.mobileFloatingBar,
    bottomOffset: shadowConfig.bottomOffset
  }
}

/**
 * Shadow DOM version of the main App component
 * Removes iframe dependencies and works directly within Shadow DOM
 */
export function ShadowApp({ config, onResize, onOpenLink, onCenterRequest }: ShadowAppProps) {
  const [content, setContent] = useState<ArticleContent | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [currentChatHeight, setCurrentChatHeight] = useState(200)

  // Convert config for AppContent compatibility
  const widgetConfig = convertConfig(config)

  // Detect content from host page on mount and URL changes
  useEffect(() => {
    shadowLogger.info('Initializing Shadow DOM app with config:', config)

    const detectContent = async () => {
      try {
        if (widgetConfig.autoDetectContent !== false) { // Default to true
          shadowLogger.debug('Starting content detection...')
          const detectedContent = ShadowContentDetector.detectAll()
          
          if (detectedContent.title || detectedContent.content) {
            setContent({
              title: detectedContent.title || 'Untitled',
              content: detectedContent.content || '',
              author: detectedContent.author || undefined,
              url: detectedContent.url
            })
            shadowLogger.info('✅ Content detected and set', {
              hasTitle: !!detectedContent.title,
              hasContent: !!detectedContent.content,
              hasAuthor: !!detectedContent.author
            })
          } else {
            shadowLogger.info('No content detected, using empty content')
            setContent(null)
          }
        } else {
          shadowLogger.info('Content detection disabled by config')
          setContent(null)
        }
      } catch (error) {
        shadowLogger.error('Content detection failed:', error)
        setContent(null)
      }
      
      setIsReady(true)
    }

    detectContent()
    
    // Monitor for URL changes to re-detect content
    let lastUrl = window.location.href
    const urlCheckInterval = setInterval(() => {
      const currentUrl = window.location.href
      if (currentUrl !== lastUrl) {
        shadowLogger.info('URL change detected in ShadowApp, re-detecting content', {
          from: lastUrl,
          to: currentUrl
        })
        lastUrl = currentUrl
        setIsReady(false)
        detectContent()
      }
    }, 1000)
    
    // Cleanup
    return () => {
      clearInterval(urlCheckInterval)
    }
  }, [config])

  // Apply theme to Shadow DOM root
  useEffect(() => {
    if (!isReady) return

    const shadowRoot = (document.querySelector(`#kwry-widget-${config.projectId}`) as any)?.shadowRoot
    if (!shadowRoot) {
      shadowLogger.warn('Shadow root not found for theme application')
      return
    }

    const applyTheme = (theme: string) => {
      const shadowContainer = shadowRoot.querySelector('#kwry-shadow-root')
      if (shadowContainer) {
        shadowContainer.setAttribute('data-theme', theme)
        
        if (theme === 'dark') {
          shadowContainer.classList.add('dark')
        } else {
          shadowContainer.classList.remove('dark')
        }
        
        shadowLogger.debug('✅ Theme applied to Shadow DOM', { theme })
      }
    }

    if (widgetConfig.theme === 'auto') {
      // Detect system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      applyTheme(mediaQuery.matches ? 'dark' : 'light')
      
      const handleChange = (e: MediaQueryListEvent) => {
        applyTheme(e.matches ? 'dark' : 'light')
      }
      
      mediaQuery.addEventListener('change', handleChange)
      shadowLogger.debug('Auto theme detection enabled')
      
      return () => mediaQuery.removeEventListener('change', handleChange)
    } else {
      applyTheme(widgetConfig.theme || 'light')
    }
  }, [widgetConfig.theme, isReady])

  // Apply custom primary color to Shadow DOM
  useEffect(() => {
    if (!isReady || !widgetConfig.primaryColor) return

    const shadowRoot = (document.querySelector(`#kwry-widget-${config.projectId}`) as any)?.shadowRoot
    if (!shadowRoot) return

    const shadowContainer = shadowRoot.querySelector('#kwry-shadow-root')
    if (shadowContainer) {
      (shadowContainer as HTMLElement).style.setProperty('--primary', widgetConfig.primaryColor)
      shadowLogger.debug('✅ Primary color applied to Shadow DOM', { color: widgetConfig.primaryColor })
    }
  }, [widgetConfig.primaryColor, isReady, config.projectId])

  // Handle chat height changes and notify parent
  const handleChatHeightChange = (newHeight: number) => {
    setCurrentChatHeight(newHeight)
    
    // Notify Shadow DOM manager about height change
    if (onResize) {
      onResize(newHeight)
    }
    
    shadowLogger.debug('Chat height changed', { newHeight })
  }

  // Handle external link opening
  const handleOpenLink = (url: string) => {
    if (onOpenLink) {
      onOpenLink(url)
    } else {
      // Fallback: open in new window/tab
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    
    shadowLogger.debug('External link opened', { url })
  }

  // Loading state while detecting content
  if (!isReady) {
    return (
      <div className="h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Kwry Widget...</p>
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientProvider projectId={config.projectId}>
        <div 
          className="w-full text-foreground flex flex-col"
          // Container will size dynamically based on content
          // Background is handled by AppContent based on floating state
        >
          <AppContent 
            config={widgetConfig}
            content={content}
            currentChatHeight={currentChatHeight}
            setCurrentChatHeight={handleChatHeightChange}
            onOpenLink={handleOpenLink}
            onCenterRequest={onCenterRequest}
          />
        </div>
      </ApiClientProvider>
    </QueryClientProvider>
  )
}